// SPDX-License-Identifier: Elastic-2.0
/**
 * iconPaths — inlined Lucide-style stroke icon path data.
 *
 * The first ~28 entries are copied verbatim from
 * `mirlai-os/design-system/ui_kits/dashboard/DashboardShell.jsx`
 * (lines 9-48). The rest are onboarding-specific additions plus a
 * handful of utility glyphs Modal/Dialog/Alert tasks will need
 * downstream (close, info, warning, x-circle, plus). Adding them
 * once here is cheaper than chasing each task back to re-edit this
 * file.
 *
 * Each entry is a `ReactNode` of one or more SVG primitive elements
 * (`<path>`, `<circle>`, `<rect>`, `<polyline>`) rendered inside a
 * 24×24 `viewBox`. Stroke properties (width, linecap, linejoin) and
 * `fill="none"` come from the parent `<svg>` in `Icon.tsx` — entries
 * that want a filled dot must set `fill="currentColor"` explicitly
 * (e.g. `help`, `wifi`).
 *
 * This file is kept as `.ts` (not `.tsx`) per the task spec; the
 * trade-off is that path elements are built via `React.createElement`
 * instead of JSX. That's a one-line wrapper (`h(...)`) below — same
 * output, no JSX runtime needed.
 */
import { createElement, type ReactNode } from "react";

/**
 * Thin alias for `React.createElement` so the entry table stays
 * readable. Keys are stable per-icon so React's reconciliation is
 * predictable; props mirror the original DashboardShell JSX 1-for-1.
 */
function h(
  tag: string,
  props: Record<string, string | number>,
  key: string | number,
): ReactNode {
  return createElement(tag, { key, ...props });
}

/**
 * Compose multiple primitives into a single `ReactNode` (a fragment-
 * substitute via an SVG `<g>` so the children render unchanged in any
 * SVG parent). Used by icons that need more than one element.
 */
function group(...children: ReactNode[]): ReactNode {
  return createElement("g", { fill: "none" }, ...children);
}

export type IconName =
  // Dashboard set (DashboardShell.jsx lines 9-48)
  | "home"
  | "sparkles"
  | "workflow"
  | "plug"
  | "user"
  | "crown"
  | "cloud"
  | "save"
  | "mic"
  | "homes"
  | "key"
  | "store"
  | "upload"
  | "chart"
  | "help"
  | "bell"
  | "search"
  | "sun"
  | "moon"
  | "lock"
  | "chevron"
  | "arrow_right"
  | "thermo"
  | "bulb"
  | "wifi"
  | "music"
  | "energy"
  | "shield"
  // Onboarding additions
  | "globe"
  | "pin"
  | "eye"
  | "eye-off"
  | "check"
  // Downstream utility additions (modals/alerts/forms)
  | "close"
  | "info"
  | "warning"
  | "x-circle"
  | "plus";

export const ICON_PATHS: Record<IconName, ReactNode> = {
  /* ── Dashboard set ────────────────────────────────────────────── */
  home: group(
    h("path", { d: "M3 12L12 3l9 9" }, 1),
    h("path", { d: "M5 10v10h14V10" }, 2),
  ),
  sparkles: h(
    "path",
    {
      d: "M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8",
    },
    1,
  ),
  workflow: group(
    h("rect", { x: 3, y: 3, width: 6, height: 6, rx: 1 }, 1),
    h("rect", { x: 15, y: 15, width: 6, height: 6, rx: 1 }, 2),
    h("path", { d: "M9 6h6a3 3 0 0 1 3 3v6" }, 3),
  ),
  plug: group(
    h("path", { d: "M9 2v6M15 2v6" }, 1),
    h("path", { d: "M7 8h10v4a5 5 0 0 1-10 0z" }, 2),
    h("path", { d: "M12 17v5" }, 3),
  ),
  user: group(
    h("circle", { cx: 12, cy: 8, r: 4 }, 1),
    h("path", { d: "M4 21a8 8 0 0 1 16 0" }, 2),
  ),
  crown: group(
    h("path", { d: "M2 18l3-9 4 4 3-7 3 7 4-4 3 9z" }, 1),
    h("path", { d: "M2 21h20" }, 2),
  ),
  cloud: h(
    "path",
    { d: "M17 18a4 4 0 0 0 0-8 6 6 0 0 0-11 1 4 4 0 0 0 0 8z" },
    1,
  ),
  save: group(
    h(
      "path",
      { d: "M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" },
      1,
    ),
    h("polyline", { points: "7 3 7 9 15 9" }, 2),
  ),
  mic: group(
    h("rect", { x: 9, y: 2, width: 6, height: 12, rx: 3 }, 1),
    h("path", { d: "M5 10a7 7 0 0 0 14 0M12 17v5" }, 2),
  ),
  homes: group(
    h("path", { d: "M3 11l9-7 9 7" }, 1),
    h("path", { d: "M5 9v11h14V9" }, 2),
  ),
  key: group(
    h("circle", { cx: 8, cy: 15, r: 4 }, 1),
    h("path", { d: "M11 12l9-9M16 7l3 3" }, 2),
  ),
  store: group(
    h("path", { d: "M3 9l1-5h16l1 5" }, 1),
    h("path", { d: "M5 9v11h14V9" }, 2),
    h("path", { d: "M9 20v-7h6v7" }, 3),
  ),
  upload: group(
    h("path", { d: "M12 3v12M7 8l5-5 5 5" }, 1),
    h("path", { d: "M3 17v3a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-3" }, 2),
  ),
  chart: group(
    h("path", { d: "M3 3v18h18" }, 1),
    h("path", { d: "M7 14l3-3 4 4 5-7" }, 2),
  ),
  help: group(
    h("circle", { cx: 12, cy: 12, r: 9 }, 1),
    h("path", { d: "M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4" }, 2),
    h("circle", { cx: 12, cy: 17, r: 0.5, fill: "currentColor" }, 3),
  ),
  bell: group(
    h("path", { d: "M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z" }, 1),
    h("path", { d: "M10 20a2 2 0 0 0 4 0" }, 2),
  ),
  search: group(
    h("circle", { cx: 11, cy: 11, r: 7 }, 1),
    h("path", { d: "M21 21l-4-4" }, 2),
  ),
  sun: group(
    h("circle", { cx: 12, cy: 12, r: 4 }, 1),
    h(
      "path",
      {
        d: "M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41",
      },
      2,
    ),
  ),
  moon: h("path", { d: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" }, 1),
  lock: group(
    h("rect", { x: 4, y: 11, width: 16, height: 10, rx: 2 }, 1),
    h("path", { d: "M8 11V7a4 4 0 0 1 8 0v4" }, 2),
  ),
  chevron: h("path", { d: "M9 6l6 6-6 6" }, 1),
  arrow_right: h("path", { d: "M5 12h14M13 5l7 7-7 7" }, 1),
  thermo: h("path", { d: "M14 14V4a2 2 0 0 0-4 0v10a4 4 0 1 0 4 0z" }, 1),
  bulb: group(
    h("path", { d: "M9 18h6M10 22h4" }, 1),
    h(
      "path",
      {
        d: "M12 2a6 6 0 0 0-4 10c1 1 1.5 2 1.5 3.5V17h5v-1.5c0-1.5.5-2.5 1.5-3.5A6 6 0 0 0 12 2z",
      },
      2,
    ),
  ),
  wifi: group(
    h("path", { d: "M2 9a14 14 0 0 1 20 0" }, 1),
    h("path", { d: "M5 13a10 10 0 0 1 14 0" }, 2),
    h("path", { d: "M8.5 16.5a5 5 0 0 1 7 0" }, 3),
    h("circle", { cx: 12, cy: 20, r: 0.5, fill: "currentColor" }, 4),
  ),
  music: group(
    h("path", { d: "M9 17V5l10-2v12" }, 1),
    h("circle", { cx: 6, cy: 17, r: 3 }, 2),
    h("circle", { cx: 16, cy: 15, r: 3 }, 3),
  ),
  energy: h("path", { d: "M13 2L4 14h7l-1 8 9-12h-7z" }, 1),
  shield: h(
    "path",
    { d: "M12 2l8 3v6c0 5-4 9-8 11-4-2-8-6-8-11V5z" },
    1,
  ),

  /* ── Onboarding additions ─────────────────────────────────────── */
  globe: group(
    h("circle", { cx: 12, cy: 12, r: 9 }, 1),
    h("path", { d: "M3 12h18" }, 2),
    h("path", { d: "M12 3a13 13 0 0 1 0 18a13 13 0 0 1 0-18z" }, 3),
  ),
  pin: group(
    h("path", { d: "M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" }, 1),
    h("circle", { cx: 12, cy: 9, r: 2.5 }, 2),
  ),
  eye: group(
    h("path", { d: "M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" }, 1),
    h("circle", { cx: 12, cy: 12, r: 3 }, 2),
  ),
  "eye-off": group(
    h("path", { d: "M3 3l18 18" }, 1),
    h(
      "path",
      {
        d: "M10.6 6.2A10.5 10.5 0 0 1 12 6c6 0 10 6 10 6a17.4 17.4 0 0 1-3.2 3.7",
      },
      2,
    ),
    h(
      "path",
      { d: "M6.1 7.2A17.6 17.6 0 0 0 2 12s4 6 10 6c1.4 0 2.7-.3 3.9-.8" },
      3,
    ),
    h("path", { d: "M9.9 9.9a3 3 0 0 0 4.2 4.2" }, 4),
  ),
  check: h("path", { d: "M4 12l5 5L20 6" }, 1),

  /* ── Utility additions (downstream tasks) ─────────────────────── */
  close: h("path", { d: "M6 6l12 12M18 6l-12 12" }, 1),
  info: group(
    h("circle", { cx: 12, cy: 12, r: 9 }, 1),
    h("path", { d: "M12 8h.01" }, 2),
    h("path", { d: "M11 12h1v5h1" }, 3),
  ),
  warning: group(
    h("path", { d: "M12 3l10 18H2L12 3z" }, 1),
    h("path", { d: "M12 10v5" }, 2),
    h("circle", { cx: 12, cy: 18, r: 0.5, fill: "currentColor" }, 3),
  ),
  "x-circle": group(
    h("circle", { cx: 12, cy: 12, r: 9 }, 1),
    h("path", { d: "M9 9l6 6M15 9l-6 6" }, 2),
  ),
  plus: h("path", { d: "M12 5v14M5 12h14" }, 1),
};

/** Sorted list of valid names — handy for tests and dev tooling. */
export const ICON_NAMES = Object.keys(ICON_PATHS) as IconName[];
